import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DetectionGroupsResponse,
  type FrontendDetectionEvent,
  type FrontendDetectionGroupCreatedEvent,
  parseDetectionGroup,
  useCreateBroadbandSuppression,
  useCreateSuppression,
  useDetectionGroups,
  useDeviceSettings,
  useDeviceStatusSocket,
  useIntl,
  useStopBroadbandSuppression,
  useStopSuppression,
} from '../../hooks';
import type {
  CreateBroadbandSuppressionError,
  StopBroadbandSuppressionError,
} from '../../hooks/useBroadbandSuppressions.js';
import { useActiveJumperSession } from '../../hooks/useJumperSessions.js';
import { useLinkedDevices } from '../../hooks/useLinkedDevices.js';
import { useLinkedDeviceSuppress } from '../../hooks/useLinkedDeviceSuppress.js';
import {
  type CreateSuppressionError,
  type SuppressionChannelPayload,
  useChangeSuppressionFrequency,
} from '../../hooks/useSuppressions.js';
import { hzToMhz } from '../../lib/frequency.js';
import { formatNumber, parseKvNumber } from '../../lib/number-format.js';
import {
  resolveDefaultBandwidthMhz,
  resolveDefaultMultiFrequencyBandwidthMhz,
  resolveDefaultMultiFrequencySignalType,
} from '../../lib/suppression-bandwidth.js';
import { cn } from '../../lib/utils.js';
import { isBenignStopError, STOPPABLE_STATUSES } from '../../services/suppressions.js';
import { scheduleInvalidation } from '../../sockets/invalidationScheduler.js';
import {
  type DetectionGroup,
  DetectionStatus,
  type DeviceCapability,
  DeviceStatus,
  SignalType,
} from '../../types';
import {
  type NonEmptySuppressionTabs,
  SuppressionForm,
  type SuppressionTab,
} from '../devices/SuppressionForm.js';
import { ActiveJumperSessionModal } from '../jumper-sessions/ActiveJumperSessionModal.js';
import { JumperSessionAlert } from '../jumper-sessions/JumperSessionAlert.js';
import { Button } from '../ui';
import {
  DetectionGroupRow,
  type DetectionGroupSuppressionSubmit,
  type DeviceSettingsStatus,
} from './DetectionGroupRow';
import {
  HIGH_PRIORITY_SUPPRESSION,
  LinkedDeviceGroupsCompactTable,
} from './LinkedDeviceGroupsCompactTable.js';
import { LinkedDevicesBar } from './LinkedDevicesBar.js';

// Statuses the stop route can reject a NON-benign request with. Both benign codes
// (SUPPRESSION_NOT_FOUND / SUPPRESSION_ALREADY_FINISHED) are absorbed before these are consulted,
// so 400 here is `ensureDeviceOnline`'s "device must be online" and 404 is "device not found".
const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;

function getStopFailureMessageId(status: number | undefined): string {
  switch (status) {
    case HTTP_BAD_REQUEST:
      return 'detections.multiFrequency.stopFailedDeviceOffline';
    case HTTP_NOT_FOUND:
      return 'detections.multiFrequency.stopFailedDeviceNotFound';
    default:
      return 'detections.multiFrequency.stopFailed';
  }
}

const getDefaultDateTime = (date: string, isEndDate: boolean = false): string => {
  if (!date) return '';
  const dateObj = new Date(date);
  dateObj.setHours(isEndDate ? 23 : 0, isEndDate ? 59 : 0, isEndDate ? 59 : 0);
  return dateObj.toISOString().slice(0, 16);
};

type DeviceDetectionsTableProps = {
  canManageSuppressions?: boolean;
  deviceCapabilities?: DeviceCapability[];
  deviceId: string;
  deviceSerialNumber: string;
  /** Absent counts as not online — an unknown status must not unlock the multi-frequency form. */
  deviceStatus?: DeviceStatus;
  isAdminUser?: boolean;
  onPageChange?: (page: number) => void;
  page?: number;
  pageSize?: number;
  showDateFilters?: boolean;
};

export type StartSuppressionParams = {
  detectionGroupCreatedAt?: string;
  detectionGroupId?: string;
  durationSec: number;
  frequencyHz: number;
  offsetHz: number;
  parentDetectionGroupCreatedAt?: string;
  parentDetectionGroupId?: string;
  priority?: number;
  signalType: SignalType;
  bandwidthHz?: number;
};

export function DeviceDetectionGroupsTable({
  canManageSuppressions = false,
  deviceCapabilities,
  deviceId,
  deviceSerialNumber,
  deviceStatus,
  isAdminUser = false,
  onPageChange,
  page = 1,
  pageSize = 20,
  showDateFilters = false,
}: DeviceDetectionsTableProps) {
  const intl = useIntl();
  const { data: deviceSettings, isLoading: isDeviceSettingsLoading } = useDeviceSettings(deviceId);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [appliedFrom, setAppliedFrom] = useState<string>('');
  const [appliedTo, setAppliedTo] = useState<string>('');
  const [linkedDevicePage, setLinkedDevicePage] = useState(1);

  const isDateFilterValid = useMemo((): boolean => {
    if (!from && !to) return false;
    if (from && to) {
      const start = new Date(from);
      const end = new Date(to);
      return !isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end;
    }
    if (from) {
      return !isNaN(new Date(from).getTime());
    }
    if (to) {
      return !isNaN(new Date(to).getTime());
    }
    return false;
  }, [from, to]);

  const { data, isLoading, error, refetch } = useDetectionGroups(
    { deviceId, page, pageSize, from: appliedFrom, to: appliedTo },
    {
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  );
  const queryClient = useQueryClient();
  const stopSuppression = useStopSuppression();
  const stopBroadbandSuppression = useStopBroadbandSuppression();
  const createSuppression = useCreateSuppression();
  const createBroadbandSuppression = useCreateBroadbandSuppression();
  const changeSuppressionFrequency = useChangeSuppressionFrequency();

  const {
    message: linkedDeviceMessage,
    clearMessage,
    suppressFromLinkedDevice,
  } = useLinkedDeviceSuppress({
    deviceId,
    onSuccess: async () => {
      await refetch();
    },
  });

  const handleActiveDeviceCleared = useCallback(() => {
    clearMessage();
    setLinkedDevicePage(1);
  }, [clearMessage]);

  const { linkedDevices, activeLinkedDeviceId, activeLinkedDevice, toggleLinkedDevice } =
    useLinkedDevices(deviceId, { onActiveDeviceCleared: handleActiveDeviceCleared });

  const handleNewDetection = useCallback(
    ({ deviceId }: FrontendDetectionEvent) => {
      if (!deviceId) return;

      scheduleInvalidation(queryClient, deviceId, ['group-detections', 'detection-groups']);
    },
    [queryClient],
  );

  const handleNewDetectionGroup = useCallback(
    ({ deviceId: eventDeviceId, detectionGroup }: FrontendDetectionGroupCreatedEvent) => {
      if (!eventDeviceId) return;

      const matchingQueries = queryClient.getQueriesData<DetectionGroupsResponse>({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key[0] !== 'detection-groups') return false;
          const params = (key[1] ?? {}) as { deviceId?: string; page?: number };
          return (
            params.deviceId === eventDeviceId && (params.page === undefined || params.page === 1)
          );
        },
      });

      for (const [queryKey, oldData] of matchingQueries) {
        if (!oldData) continue;

        const params = (queryKey[1] ?? {}) as {
          pageSize?: number;
          parentDetectionGroupId?: string;
        };
        const pageSize = params.pageSize ?? 20;

        if (params.parentDetectionGroupId !== undefined) {
          if (detectionGroup.parentDetectionGroupId !== params.parentDetectionGroupId) {
            continue;
          }
        } else if (detectionGroup.parentDetectionGroupId) {
          continue;
        }

        if (oldData.groups.some((existingGroup) => existingGroup.id === detectionGroup.id)) {
          continue;
        }

        const insertIndex = oldData.groups.findIndex(
          (group) => group.status !== DetectionStatus.SUPPRESSION_IN_PROGRESS,
        );

        // Second ingress into this query's cache, so it gets the same guard as the list request.
        const parsedGroup = parseDetectionGroup(detectionGroup);
        const newGroups = [...oldData.groups];
        if (insertIndex === -1) {
          newGroups.push(parsedGroup);
        } else {
          newGroups.splice(insertIndex, 0, parsedGroup);
        }

        queryClient.setQueryData<DetectionGroupsResponse>(queryKey, {
          ...oldData,
          groups: newGroups.slice(0, pageSize),
          total: oldData.total + 1,
        });
      }

      scheduleInvalidation(queryClient, eventDeviceId, ['detection-groups']);
    },
    [queryClient],
  );

  useDeviceStatusSocket(
    {
      onNewDetection: handleNewDetection,
      onNewDetectionGroup: handleNewDetectionGroup,
    },
    useMemo(() => [deviceId], [deviceId]),
  );

  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [jumperModalOpen, setJumperModalOpen] = useState<boolean>(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [pendingStopGroupIds, setPendingStopGroupIds] = useState<Set<string>>(new Set());
  // Surfaces a non-benign multi-frequency stop failure, or a failed repeat-suppression attempt,
  // to the operator. Classifying benign from non-benign is only worth doing if the non-benign half
  // is visible, and before this the distinction had no user-visible consequence — the button just
  // re-enabled with nothing on screen saying the jammer was never told to stop.
  const [suppressionActionError, setSuppressionActionError] = useState<string | null>(null);
  const [pendingChangeFrequencyGroupIds, setPendingChangeFrequencyGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const groups = useMemo(() => data?.groups ?? [], [data?.groups]);
  // Page-scoped invariant: children whose parent is off the current page are
  // rendered as top-level rows (no synthetic parent injected).
  const childrenByParentId = useMemo(() => {
    const m = new Map<string, DetectionGroup[]>();
    for (const g of groups) {
      if (g.parentDetectionGroupId) {
        const arr = m.get(g.parentDetectionGroupId) ?? [];
        arr.push(g);
        m.set(g.parentDetectionGroupId, arr);
      }
    }
    return m;
  }, [groups]);
  const topLevelGroups = useMemo(() => {
    const ids = new Set(groups.map((g) => g.id));
    return groups.filter((g) => !g.parentDetectionGroupId || !ids.has(g.parentDetectionGroupId));
  }, [groups]);
  const toggleParentExpansion = useCallback((groupId: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleGroupRow = useCallback((groupId: string) => {
    setExpandedGroup((prev) => (prev === groupId ? null : groupId));
  }, []);
  const handleToggleLinkedDevice = useCallback(
    (linkedDeviceId: string) => {
      clearMessage();
      setLinkedDevicePage(1);
      toggleLinkedDevice(linkedDeviceId);
    },
    [clearMessage, toggleLinkedDevice],
  );

  useEffect(() => {
    if (isLoading || !data) return;

    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) {
      onPageChange?.(maxPage);
    } else if (page < 1) {
      onPageChange?.(1);
    }
  }, [data, total, pageSize, isLoading, onPageChange, page]);

  const handleStopSuppression = useCallback(
    async (groupId: string, frequency: number, signalType: string, createdAt: string) => {
      setPendingStopGroupIds((prev) => {
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });

      try {
        await stopSuppression.mutateAsync({
          deviceId,
          payload: {
            channels: [
              {
                channelFrequency: frequency,
                type: signalType as SignalType,
                detectionGroupId: groupId,
                detectionGroupCreatedAt: createdAt,
              },
            ],
          },
        });
      } catch (e) {
        console.error('Failed to stop suppression', e);
        setPendingStopGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
      }
    },
    [stopSuppression, deviceId],
  );

  // Hold the lock on success: a 200 only means the STOP command reached NATS, and the
  // group stays stoppable until the device report lands — clearing here would let a
  // second click send a duplicate stop. The sweeper releases it on the status change.
  const handleStopMultiFrequencySuppression = useCallback(
    async (groupId: string, suppressionId: string) => {
      setSuppressionActionError(null);
      setPendingStopGroupIds((prev) => {
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });

      try {
        await stopBroadbandSuppression.mutateAsync({ deviceId, suppressionId });
      } catch (error) {
        if (isBenignStopError(error)) {
          // `onSuccess` never runs on a rejection, so re-sync here; `detection-groups` is
          // included because the table renders from that query.
          queryClient.invalidateQueries({ queryKey: ['broadband-suppressions'] });
          queryClient.invalidateQueries({
            queryKey: ['broadband-suppressions', { deviceId }],
          });
          queryClient.invalidateQueries({ queryKey: ['all-suppressions'] });
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return (
                Array.isArray(key) &&
                key[0] === 'detection-groups' &&
                (key[1] as { deviceId?: string } | undefined)?.deviceId === deviceId
              );
            },
          });
        } else {
          // Non-benign: e.g. the controller's `ensureDeviceOnline` guard rejects the stop because
          // the device dropped offline mid-suppression. Surface it — silently re-enabling the
          // button here would leave the operator with no indication the jammer was never told to
          // stop.
          console.error('Failed to stop multi-frequency suppression', error);
          // Localise from the `status` the hook attaches -- NOT from `error.message`.
          // `useStopBroadbandSuppression` discards the axios error and re-throws a synthetic
          // `new Error('Failed to stop broadband suppression.')` in `useBroadbandSuppressions.ts`,
          // so `extractErrorMessage` would return that hardcoded English constant every time and
          // this banner would never be translated.
          const status = (error as StopBroadbandSuppressionError | undefined)?.status;
          setSuppressionActionError(intl.formatMessage({ id: getStopFailureMessageId(status) }));
        }
        // Release the lock on the error path — the sweeper can't clear an id whose group status
        // never changed.
        setPendingStopGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
      }
    },
    [stopBroadbandSuppression, deviceId, queryClient, intl],
  );

  // Deliberately NO auto-dismiss. The 4s timer this used to share with useLinkedDeviceSuppress is
  // parity with a SUCCESS confirmation -- that hook only ever sets a success string, never an error
  // -- so it does not transfer to "the jammer was never told to stop".
  // The notice is cleared at the start of the next stop attempt instead, so it cannot get stuck.

  const handleChangeFrequency = useCallback(
    async ({
      groupId,
      frequency,
      signalType,
      duration,
    }: {
      groupId: string;
      frequency: number;
      signalType: string;
      duration: number;
    }) => {
      setPendingChangeFrequencyGroupIds((prev) => {
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });

      try {
        await changeSuppressionFrequency.mutateAsync({
          deviceId,
          payload: {
            channelFrequency: frequency,
            type: signalType as SignalType,
            duration: duration > 0 ? duration : (deviceSettings?.suppressionTime ?? 0),
            detectionGroupId: groupId,
          },
        });

        await refetch();
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to change suppression frequency');
      } finally {
        setPendingChangeFrequencyGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
      }
    },
    [changeSuppressionFrequency, deviceId, deviceSettings?.suppressionTime, refetch],
  );

  useEffect(() => {
    setExpandedGroup(null);
    setLinkedDevicePage(1);
  }, [deviceId]);

  useEffect(() => {
    setPendingStopGroupIds((prev) => {
      const activeIds = new Set(
        groups
          .filter((group) => STOPPABLE_STATUSES.includes(group.status))
          .map((group) => group.id),
      );
      const next = new Set([...prev].filter((id) => activeIds.has(id)));
      const unchanged =
        next.size === prev.size && [...next].every((id) => prev.has(id) && activeIds.has(id));
      return unchanged ? prev : next;
    });
  }, [groups]);

  const startSuppression = useCallback(
    async ({
      frequencyHz,
      signalType,
      durationSec,
      offsetHz = 0,
      parentDetectionGroupCreatedAt,
      parentDetectionGroupId,
      priority,
      bandwidthHz,
      detectionGroupId,
      detectionGroupCreatedAt,
    }: StartSuppressionParams) => {
      const channelPayload: SuppressionChannelPayload = {
        channelFrequency: frequencyHz,
        type: signalType,
        duration: durationSec,
        offset: offsetHz,
      };

      if (priority !== undefined) {
        channelPayload.priority = priority;
      }
      if (bandwidthHz !== undefined) {
        channelPayload.bandwidth = bandwidthHz;
      }
      if (detectionGroupId) {
        channelPayload.detectionGroupId = detectionGroupId;
      }
      if (detectionGroupCreatedAt) {
        channelPayload.detectionGroupCreatedAt = detectionGroupCreatedAt;
      }
      if (parentDetectionGroupId) {
        channelPayload.parentDetectionGroupId = parentDetectionGroupId;
      }
      if (parentDetectionGroupCreatedAt) {
        channelPayload.parentDetectionGroupCreatedAt = parentDetectionGroupCreatedAt;
      }

      await createSuppression.mutateAsync({
        deviceId,
        payload: {
          channels: [channelPayload],
        },
      });
      await refetch();
    },
    [createSuppression, deviceId, refetch],
  );

  const handleStartSuppression = useCallback(
    async ({
      group,
      durationSec,
      isPriority,
      bandwidthHz,
      detectionGroupId,
      detectionGroupCreatedAt,
    }: DetectionGroupSuppressionSubmit) => {
      if (!deviceSettings) return;

      const maxSuppressionTime = deviceSettings.maxSuppressionTime;
      if (durationSec < 1 || durationSec > maxSuppressionTime) {
        return;
      }

      const offsetHz = parseKvNumber(group.offset) ?? 0;
      const normalizedType = (group.signalType as SignalType) ?? SignalType.ANALOG;

      try {
        await startSuppression({
          frequencyHz: group.frequency,
          signalType: normalizedType,
          durationSec,
          offsetHz,
          priority: isPriority ? HIGH_PRIORITY_SUPPRESSION : undefined,
          bandwidthHz,
          detectionGroupId,
          detectionGroupCreatedAt,
        });
      } catch (e) {
        console.error('Failed to start suppression', e);
      }
    },
    [deviceSettings, startSuppression],
  );

  const handleRepeatSuppression = useCallback(
    async (group: DetectionGroup) => {
      const lastSuppression = group.lastSuppression;
      // Defensive: the row disables Repeat until settings resolve and hides it for an
      // out-of-bounds duration, so these should not fire. If one ever does, a banner beats a bare
      // return -- a click that does nothing at all is indistinguishable from a hung request.
      if (!deviceSettings || !lastSuppression || lastSuppression.duration == null) {
        setSuppressionActionError(
          intl.formatMessage({ id: 'detections.table.repeatSuppressionFailed' }),
        );
        return;
      }

      // Mirrors `handleStartSuppression`'s own bound: the device's `maxSuppressionTime` may have
      // been lowered since the snapshot was recorded.
      const maxSuppressionTime = deviceSettings.maxSuppressionTime;
      if (lastSuppression.duration < 1 || lastSuppression.duration > maxSuppressionTime) {
        setSuppressionActionError(
          intl.formatMessage({ id: 'detections.table.repeatSuppressionFailed' }),
        );
        return;
      }

      setSuppressionActionError(null);
      // Off-enum `lastSuppression.type` (device-auto or historical) cannot be replayed — the
      // backend DTO is `@IsEnum(SignalType)` — so fall back to the group's own current type.
      const normalizedType = Object.values(SignalType).includes(lastSuppression.type as SignalType)
        ? (lastSuppression.type as SignalType)
        : group.signalType;

      try {
        await startSuppression({
          // The group's CURRENT frequency, not the historical one the last suppression ran on --
          // "Edit frequency" may have moved the group since, and the group is the retarget.
          frequencyHz: group.frequency,
          signalType: normalizedType,
          durationSec: lastSuppression.duration,
          offsetHz: parseKvNumber(group.offset) ?? 0,
          bandwidthHz: lastSuppression.bandwidth ?? undefined,
          // Mandatory: without both, ForceSuppressionSaga logs an error, clears itself, and never
          // dispatches the device command -- the HTTP call returns 200 and nothing happens.
          detectionGroupId: group.id,
          detectionGroupCreatedAt: group.createdAt,
        });
      } catch (error) {
        console.error('Failed to repeat suppression', error);
        const serverMessage = (error as CreateSuppressionError | null)?.serverMessage;
        setSuppressionActionError(
          serverMessage ?? intl.formatMessage({ id: 'detections.table.repeatSuppressionFailed' }),
        );
      }
    },
    [startSuppression, deviceSettings, intl],
  );

  // `isPending` from `useCreateBroadbandSuppression` is React Query's own subscriber state, set
  // on `mutateAsync` but flipped to the button's `disabled` prop only on the next render --
  // subscriber notification and re-render are scheduled, not synchronous. Two clicks dispatched
  // from the same rendered state can both enter this handler before either sees the flip, each
  // issuing a real device jamming command. This ref is a synchronous second guard on top of the
  // `isPending`-driven disabled prop (left as-is, not replaced): the ordinary repeat
  // (`handleRepeatSuppression`) has the same window but doesn't need one there -- a
  // duplicate suppression there is a wasted request, not a duplicate device command.
  const isRepeatMultiFrequencyInFlightRef = useRef(false);

  const handleRepeatMultiFrequencySuppression = useCallback(
    async (group: DetectionGroup) => {
      if (isRepeatMultiFrequencyInFlightRef.current) return;

      setSuppressionActionError(null);
      const snapshot = group.multiFrequencySuppression;
      // Defensive: the row disables the button until the snapshot resolves, so this should not
      // fire. If it does, a banner beats a bare return -- a click that does nothing at all is
      // indistinguishable from a hung request.
      if (!snapshot) {
        setSuppressionActionError(
          intl.formatMessage({ id: 'detections.multiFrequency.repeatFailed' }),
        );
        return;
      }

      isRepeatMultiFrequencyInFlightRef.current = true;
      try {
        await createBroadbandSuppression.mutateAsync({
          deviceId,
          payload: {
            mode: 'multi',
            type: snapshot.type,
            // The backend already sorts on write (`normalizeForCreate`), so this only matters for
            // legacy rows created before that landed. Sort a COPY -- `snapshot` is React Query
            // cache data.
            channelFrequencyList: [...snapshot.channelFrequencyList].sort((a, b) => a - b),
            bandwidth: snapshot.bandwidth,
            stepStandingTimeMs: snapshot.stepStandingTimeMs,
            duration: snapshot.duration,
          },
        });
        // No `DetectionGroupCreatedEvent` is published for a synthetic group, so no
        // `new_detection_group` WS frame ever arrives to trigger `handleNewDetectionGroup` --
        // refetch explicitly instead of waiting for the 30s poll.
        await refetch();
      } catch (error) {
        console.error('Failed to repeat multi-frequency suppression', error);
        const serverMessage = (error as CreateBroadbandSuppressionError | null)?.serverMessage;
        setSuppressionActionError(
          serverMessage ?? intl.formatMessage({ id: 'detections.multiFrequency.repeatFailed' }),
        );
      } finally {
        isRepeatMultiFrequencyInFlightRef.current = false;
      }
    },
    [createBroadbandSuppression, deviceId, intl, refetch],
  );

  const handleApplyDateFilter = () => {
    const fromDate = from ? getDefaultDateTime(from, false) : '';
    const toDate = to ? getDefaultDateTime(to, true) : '';
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
    onPageChange?.(1);
  };

  const handleClearDateFilter = () => {
    setFrom('');
    setTo('');
    setAppliedFrom('');
    setAppliedTo('');
    onPageChange?.(1);
  };

  const handleLinkedDeviceSuppressGroup = useCallback(
    (group: DetectionGroup, durationSec: number, priority?: number) => {
      if (!activeLinkedDevice) return;
      return suppressFromLinkedDevice(group, activeLinkedDevice.serial, durationSec, priority);
    },
    [activeLinkedDevice, suppressFromLinkedDevice],
  );

  const deviceSuppressionDuration = deviceSettings?.suppressionTime ?? 0;
  const deviceMaxSuppressionTime = deviceSettings?.maxSuppressionTime ?? 0;
  // A failed settings fetch is not a slow one: without this split the Repeat button would sit
  // disabled under "still loading" forever once the request errors out.
  const deviceSettingsStatus: DeviceSettingsStatus = deviceSettings
    ? 'ready'
    : isDeviceSettingsLoading
      ? 'loading'
      : 'unavailable';
  const jumperModeEnabled = deviceSettings?.jumperMode === true;
  const activeJumperSession = useActiveJumperSession(deviceId);
  // `!isError` as well as `!session`: the query keeps its last successful value when a refetch
  // fails, and for a device that never had a session that value is `null` — indistinguishable
  // from a confirmed "none running", which is exactly the state that would let a second session
  // be started over a live one.
  const canStartJumperSession =
    canManageSuppressions &&
    jumperModeEnabled &&
    activeJumperSession.isFetched &&
    !activeJumperSession.isError &&
    !activeJumperSession.session;
  const minAllowedMhz =
    deviceSettings?.minAllowedFrequency != null
      ? hzToMhz(deviceSettings.minAllowedFrequency)
      : null;
  const maxAllowedMhz =
    deviceSettings?.maxAllowedFrequency != null
      ? hzToMhz(deviceSettings.maxAllowedFrequency)
      : null;

  // Visibility is capability + permission only — device status gates the controls, not the tab.
  const canUseMultiFrequencySuppressions =
    canManageSuppressions &&
    (deviceCapabilities?.includes('multi_frequency_suppressions') ?? false);
  // Shared with the multi-frequency Stop button below: a click while
  // offline is a guaranteed 400 from the controller's `ensureDeviceOnline` guard.
  const isDeviceOffline = deviceStatus !== DeviceStatus.ONLINE;

  const canUseJumperSessions = canManageSuppressions && jumperModeEnabled;

  // Every term is a permission or a device capability, never a runtime session state:
  // `useActiveJumperSession` refetches on window focus and reconnect, so a set derived from
  // `canStartJumperSession` would drop the Jumper tab the moment a session begins anywhere, and
  // Radix unmounts the TabsContent — an operator who alt-tabbed away mid-entry loses everything
  // typed, with no warning. A live session disables the panel instead (`jumperDisabled` below).
  //
  // `regular` is unconditional: it replaced the editable draft row the table used to append, so it
  // is the manual-suppression affordance for every device the operator may act on — and leading
  // makes the non-empty tuple honest without an `as` cast. The solid and hopping tabs stay off:
  // they were reachable here exactly when `multi_frequency_suppressions` was present, and that
  // capability belongs to the kvitka family alone (`deriveDeviceModel`), which does not execute
  // single-band broadband suppressions. They stay on the Sunflower broadband dialog, whose devices
  // carry `broadband_suppressions` instead.
  const suppressionsSectionTabs = useMemo<NonEmptySuppressionTabs>(() => {
    const optionalTabs: SuppressionTab[] = [];
    if (canUseMultiFrequencySuppressions) optionalTabs.push('multi');
    if (canUseJumperSessions) optionalTabs.push('jumper');
    return ['regular', ...optionalTabs];
  }, [canUseMultiFrequencySuppressions, canUseJumperSessions]);

  // Offline only. Defence in depth rather than the live gate — `canManageSuppressions` is computed
  // by `services/device-permissions.ts:17`, which already returns false for an offline device, so
  // the section never renders in that state today.
  //
  // Deliberately NOT the Sunflower dialog's `canUseForm`, which also requires a non-null
  // `maxSuppressionTime`: this section replaced the table's editable draft row, which was gated on
  // permission alone, and `buildManualSuppressionSchema` simply drops the duration cap when the
  // device reports none. Adding that term here would take manual suppression away entirely — every
  // field and the submit button disabled inside a fully visible section, with no explanation and no
  // other route to one — for a device that is online and that the operator may act on.
  const isSuppressionFormDisabled = isDeviceOffline;

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between gap-4 mb-4 shrink-0">
          <div className="min-w-0 space-y-2">
            <h2 className="text-lg font-semibold">
              {intl.formatMessage({ id: 'detections.title' }, { deviceSerialNumber })}
            </h2>
            <LinkedDevicesBar
              linkedDevices={linkedDevices}
              activeLinkedDeviceId={activeLinkedDeviceId}
              onToggle={handleToggleLinkedDevice}
            />
          </div>
          <div className="flex gap-2">
            {canManageSuppressions && isDeviceSettingsLoading && (
              <span className="self-center text-xs text-foreground">
                {intl.formatMessage({ id: 'common.loading' })}
              </span>
            )}
          </div>
        </div>

        {/* A stop error OUTRANKS an informational linked-device message. Both auto-dismiss after
            4s, so with the reverse precedence an error raised while an info message is on screen
            would be masked until that message cleared -- by which point the error's own timer has
            nearly expired and the operator never sees that the jammer was not stopped, which is
            the exact failure this banner exists to prevent. */}
        {(suppressionActionError ?? linkedDeviceMessage) && (
          // `key` remounts on the info <-> error switch: mutating role/aria-live on a node an
          // assistive tech has already latched as a polite status region does not reliably
          // re-announce, while a fresh node inserted as role="alert" does.
          <div
            key={suppressionActionError ? 'action-error' : 'linked-info'}
            role={suppressionActionError ? 'alert' : 'status'}
            aria-live={suppressionActionError ? 'assertive' : 'polite'}
            className={cn(
              'mb-3 rounded-md border px-3 py-2 text-xs shrink-0',
              suppressionActionError
                ? // role="alert" buys nothing for a SIGHTED operator: without this the "jammer was
                  // not stopped" notice is the same neutral grey box as "suppression created".
                  'border-destructive bg-destructive-light text-destructive-dark'
                : 'border-border bg-background-light text-foreground',
            )}
          >
            {suppressionActionError ?? linkedDeviceMessage}
          </div>
        )}

        {activeLinkedDevice && (
          <div className="mb-4 shrink-0">
            <LinkedDeviceGroupsCompactTable
              canManageSuppressions={canManageSuppressions && Boolean(deviceSettings)}
              defaultDurationSec={deviceSuppressionDuration}
              deviceId={activeLinkedDevice.id}
              deviceSerialNumber={activeLinkedDevice.serial}
              maxSuppressionTime={deviceMaxSuppressionTime}
              page={linkedDevicePage}
              pageSize={5}
              onPageChange={setLinkedDevicePage}
              onSuppressGroup={handleLinkedDeviceSuppressGroup}
            />
          </div>
        )}

        {showDateFilters && (
          <div className="flex gap-4 items-end bg-background-light p-4 rounded-lg mb-4 shrink-0">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'detections.filter.fromDate' })}
              </label>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-2 border border-background-dark rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">
                {intl.formatMessage({ id: 'detections.filter.toDate' })}
              </label>
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-2 border border-background-dark rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleApplyDateFilter}
              disabled={!isDateFilterValid}
            >
              {intl.formatMessage({ id: 'common.apply' })}
            </Button>
            {(appliedFrom || appliedTo) && (
              <Button variant="outline" size="sm" onClick={handleClearDateFilter}>
                {intl.formatMessage({ id: 'common.reset' })}
              </Button>
            )}
          </div>
        )}

        <JumperSessionAlert deviceId={deviceId} onOpenDetails={() => setJumperModalOpen(true)} />
        <ActiveJumperSessionModal
          open={jumperModalOpen}
          onOpenChange={setJumperModalOpen}
          deviceId={deviceId}
          canManageSuppressions={canManageSuppressions}
        />
        {/* Capped and self-scrolling, replacing the `max-h-[70vh] overflow-auto` the deleted dialog
            carried. Every sibling above the table is `shrink-0`, so an uncapped section here comes
            straight out of the table's `flex-1` share and can starve it to nothing. */}
        {canManageSuppressions && deviceSettings && (
          <div
            // `tabIndex={-1}`: click-focusable but not tab-reachable. The Detections dialog exempts
            // Escape by asking whether the focused node sits inside this section, and a click on
            // the section's own padding otherwise blurs to `<body>`, from which Radix's FocusScope
            // pulls focus up to the dialog container — an ANCESTOR, so the exemption misses and the
            // next Escape discards the entry.
            tabIndex={-1}
            className="mb-4 shrink-0 max-h-[30vh] overflow-auto outline-none"
            data-testid="suppressions-section"
          >
            <SuppressionForm
              deviceId={deviceId}
              tabs={suppressionsSectionTabs}
              initialTab="regular"
              defaultSuppressionTime={deviceSettings.suppressionTime ?? null}
              maxSuppressionTime={deviceSettings.maxSuppressionTime ?? null}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              defaultAnalogBandwidthMhz={resolveDefaultBandwidthMhz(
                SignalType.ANALOG,
                deviceSettings,
              )}
              defaultDigitalBandwidthMhz={resolveDefaultBandwidthMhz(
                SignalType.DIGITAL,
                deviceSettings,
              )}
              defaultAnalogMultiFrequencyBandwidthMhz={resolveDefaultMultiFrequencyBandwidthMhz(
                SignalType.ANALOG,
                deviceSettings,
              )}
              defaultDigitalMultiFrequencyBandwidthMhz={resolveDefaultMultiFrequencyBandwidthMhz(
                SignalType.DIGITAL,
                deviceSettings,
              )}
              defaultMultiFrequencySignalType={resolveDefaultMultiFrequencySignalType(
                deviceSettings,
              )}
              // Mirrors the Sunflower dialog's `canUseForm` — see `isSuppressionFormDisabled`.
              disabled={isSuppressionFormDisabled}
              // Where the tab is gated on capability, the PANEL is gated on whether a session can
              // actually be started right now — a running session, a not-yet-settled first read or
              // a failed one all lock the fields in place rather than unmounting them under the
              // operator. `JumperSessionAlert` above already says a session is running.
              jumperDisabled={deviceStatus !== DeviceStatus.ONLINE || !canStartJumperSession}
              onSuppressionCreated={() => {
                void refetch();
              }}
            />
          </div>
        )}

        {/* `min-h-0`, so the table can absorb whatever the shrink-0 siblings above it claim. A
            floor here is a floor flexbox cannot shrink past: with the section (up to 30vh), the
            header, the jumper alert and the pagination row all fixed, a 160px floor overflows the
            dialog's `overflow-hidden` below roughly 900px of viewport — and clips the pagination
            row rather than scrolling to it, stranding the operator on whichever page they are on. */}
        <div className="flex-1 min-h-0 rounded-lg overflow-hidden flex flex-col">
          <div className="overflow-auto flex-1">
            <table className="w-full caption-bottom text-sm">
              <thead className="bg-background-light sticky top-0 z-10">
                <tr className="border-b">
                  <th className="h-12 px-4 font-medium text-foreground bg-background-light">
                    <div className="flex items-center justify-center">
                      {intl.formatMessage({ id: 'detections.table.direction' })}
                    </div>
                  </th>
                  <th className="h-12 px-4 text-center align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.type' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.status' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.frequency' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.offset' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.signalLevel' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.snr' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.pattern' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.lastSeen' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.details' })}
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-foreground bg-background-light">
                    {intl.formatMessage({ id: 'detections.table.actions' })}
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {isLoading && (
                  <tr className="border-b transition-colors hover:bg-slate-100/50">
                    <td
                      colSpan={12}
                      className="p-4 align-middle bg-background-light pl-8 text-center"
                    >
                      {intl.formatMessage({ id: 'detections.loadingDetections' })}
                    </td>
                  </tr>
                )}

                {error && (
                  <tr className="border-b transition-colors hover:bg-slate-100/50">
                    <td
                      colSpan={12}
                      className="p-4 align-middle bg-background-light pl-8 text-destructive py-2 flex items-center gap-2"
                    >
                      {intl.formatMessage({ id: 'detections.failedToLoad' })}:{' '}
                      {error instanceof Error
                        ? error.message
                        : intl.formatMessage({ id: 'common.unknownError' })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                        className="text-xs"
                      >
                        {intl.formatMessage({ id: 'common.retry' })}
                      </Button>
                    </td>
                  </tr>
                )}

                {groups.length === 0 && (
                  <tr className="border-b transition-colors hover:bg-slate-100/50">
                    <td
                      colSpan={12}
                      className="p-4 align-middle bg-background-light pl-8 text-center"
                    >
                      {intl.formatMessage({ id: 'detections.noDetectionsForDevice' })}
                    </td>
                  </tr>
                )}

                {topLevelGroups.flatMap((group) => {
                  const linkedChildren = childrenByParentId.get(group.id) ?? [];
                  const childrenOpen = expandedParents.has(group.id);
                  const rows = [
                    <DetectionGroupRow
                      canManageSuppressions={canManageSuppressions}
                      isAdminUser={isAdminUser}
                      changeFrequencyPending={pendingChangeFrequencyGroupIds.has(group.id)}
                      colSpan={12}
                      createSuppressionPending={createSuppression.isPending}
                      defaultSuppressionDuration={deviceSuppressionDuration}
                      defaultBandwidthMhz={resolveDefaultBandwidthMhz(
                        group.signalType,
                        deviceSettings,
                      )}
                      deviceId={deviceId}
                      isExpanded={expandedGroup === group.id}
                      group={group}
                      key={`${group.id}-${group.createdAt}`}
                      linkedChildren={linkedChildren}
                      childrenOpen={childrenOpen}
                      onToggleChildren={
                        linkedChildren.length ? () => toggleParentExpansion(group.id) : undefined
                      }
                      maxSuppressionTime={deviceMaxSuppressionTime}
                      deviceSettingsStatus={deviceSettingsStatus}
                      onChangeFrequency={handleChangeFrequency}
                      onStartSuppression={handleStartSuppression}
                      onStopSuppression={handleStopSuppression}
                      onStopMultiFrequencySuppression={handleStopMultiFrequencySuppression}
                      onRepeatSuppression={handleRepeatSuppression}
                      onRepeatMultiFrequencySuppression={handleRepeatMultiFrequencySuppression}
                      createBroadbandSuppressionPending={createBroadbandSuppression.isPending}
                      onToggleGroupRow={toggleGroupRow}
                      stopSuppressionPending={pendingStopGroupIds.has(group.id)}
                      isDeviceOffline={isDeviceOffline}
                    />,
                  ];
                  if (childrenOpen && linkedChildren.length) {
                    for (const child of linkedChildren) {
                      rows.push(
                        <DetectionGroupRow
                          canManageSuppressions={canManageSuppressions}
                          isAdminUser={isAdminUser}
                          changeFrequencyPending={pendingChangeFrequencyGroupIds.has(child.id)}
                          colSpan={12}
                          createSuppressionPending={createSuppression.isPending}
                          defaultSuppressionDuration={deviceSuppressionDuration}
                          defaultBandwidthMhz={resolveDefaultBandwidthMhz(
                            child.signalType,
                            deviceSettings,
                          )}
                          deviceId={deviceId}
                          isExpanded={expandedGroup === child.id}
                          group={child}
                          key={`${child.id}-${child.createdAt}`}
                          indentLevel={1}
                          maxSuppressionTime={deviceMaxSuppressionTime}
                          deviceSettingsStatus={deviceSettingsStatus}
                          onChangeFrequency={handleChangeFrequency}
                          onStartSuppression={handleStartSuppression}
                          onStopSuppression={handleStopSuppression}
                          onStopMultiFrequencySuppression={handleStopMultiFrequencySuppression}
                          onRepeatSuppression={handleRepeatSuppression}
                          onRepeatMultiFrequencySuppression={handleRepeatMultiFrequencySuppression}
                          createBroadbandSuppressionPending={createBroadbandSuppression.isPending}
                          onToggleGroupRow={toggleGroupRow}
                          stopSuppressionPending={pendingStopGroupIds.has(child.id)}
                          isDeviceOffline={isDeviceOffline}
                        />,
                      );
                    }
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between py-2 text-xs mt-4 shrink-0">
          <span>
            {intl.formatMessage(
              { id: 'detections.table.showing' },
              {
                start:
                  groups.length === 0
                    ? '0'
                    : formatNumber((page - 1) * pageSize + 1, { maximumFractionDigits: 0 }),
                end: formatNumber(Math.min(page * pageSize, total), {
                  maximumFractionDigits: 0,
                }),
                total: formatNumber(total, { maximumFractionDigits: 0 }),
              },
            )}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isLoading}
              onClick={() => onPageChange?.(Math.max(1, page - 1))}
            >
              {intl.formatMessage({ id: 'common.previous' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || isLoading}
              onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
            >
              {intl.formatMessage({ id: 'common.next' })}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
